import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import apiService from '../services/api.service';

type Action = 'approve' | 'reject' | 'return';

const ACTIONS: Array<{ value: Action; label: string; color: 'success' | 'error' | 'warning' }> = [
  { value: 'approve', label: 'Approve', color: 'success' },
  { value: 'reject', label: 'Reject', color: 'error' },
  { value: 'return', label: 'Return to initiator', color: 'warning' },
];

const money = (currency: string, value: any) =>
  `${currency ? `${currency} ` : ''}${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}`;

const decisionLabel = (status: string, remarks: string) => {
  if (status === 'ardApproved') return 'Approved';
  if (status === 'ardNotApproved') return /^RETURNED TO INITIATOR:/i.test(remarks || '') ? 'Returned' : 'Rejected';
  return status;
};

const STATE_MESSAGES: Record<string, { severity: 'info' | 'warning' | 'error'; text: string }> = {
  used: { severity: 'info', text: 'This approval link has already been used.' },
  decided: { severity: 'info', text: 'This approval step is no longer waiting for you. It was already decided, possibly directly in SAP B1.' },
  expired: { severity: 'warning', text: 'This approval link has expired. Please approve in SAP B1.' },
  locked: { severity: 'error', text: 'Too many wrong passwords were entered. This link is locked; please approve in SAP B1.' },
  review: { severity: 'warning', text: 'Review mode: this is a preview of the e-mail approval. Decisions are disabled; approvals still happen in SAP B1.' },
  disabled: { severity: 'warning', text: 'E-mail approval is switched off for this company. Please approve in SAP B1.' },
};

const SapPoApprovalAction: React.FC = () => {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const initial = (['approve', 'reject', 'return'] as Action[]).includes(params.get('action') as Action) ? (params.get('action') as Action) : 'approve';
  const [action, setAction] = React.useState<Action>(initial);
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<any | null>(null);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [comments, setComments] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res: any = await apiService.resolveSapPoApproval(token);
        if (cancelled) return;
        setData(res?.data || null);
      } catch (e: any) {
        if (!cancelled) setError(String(e?.response?.data?.message || 'Unable to open this approval link'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const needsComment = action !== 'approve';
  // Decisions use only the SAP password an administrator saved for the approver in PRPO.
  const hasPassword = !!data?.password?.saved;
  const canDecide = data?.state === 'pending' && !success && hasPassword;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (needsComment && comments.trim().length < 3) { setError('Please enter a comment for the initiator.'); return; }
    setSubmitting(true);
    try {
      const res: any = await apiService.decideSapPoApproval(token, { action, comments: comments.trim() });
      setSuccess(String(res?.message || 'Your decision was recorded in SAP B1.'));
    } catch (err: any) {
      const r = err?.response?.data || {};
      if (r.code === 'NO_SAVED_PASSWORD' || r.code === 'SAVED_PASSWORD_REJECTED') {
        setData((x: any) => ({ ...x, password: { saved: false, rejected: r.code === 'SAVED_PASSWORD_REJECTED' } }));
      } else {
        setError(String(r.message || 'The decision could not be recorded.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const draft = data?.draft;
  const view = data?.view;
  const stateMsg = data && data.state !== 'pending' ? STATE_MESSAGES[data.state] : null;

  return (
    <Box
      component="main"
      id="main-content"
      sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', justifyContent: 'center', p: { xs: 1, sm: 2 } }}
    >
      <Paper sx={{ width: '100%', maxWidth: 980, p: { xs: 2, sm: 3 }, alignSelf: 'flex-start' }} aria-labelledby="sap-approval-heading">
        {loading ? (
          <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: 240 }} role="status" aria-live="polite">
            <CircularProgress aria-label="Loading purchase order" />
            <Typography variant="body2">Loading the purchase order from SAP B1…</Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Box>
              <Typography component="h1" id="sap-approval-heading" variant="h5" sx={{ fontWeight: 700, fontSize: { xs: 19, sm: 22, md: 24 } }}>
                Purchase Order Approval
              </Typography>
              {data ? (
                <Typography variant="body2" color="text.secondary">
                  {data.companyName} · SAP draft no. <strong>{draft?.docNum}</strong> (draft key {draft?.draftEntry}) · {data.templateName} · stage {data.stageIndex || '?'} of {data.stageTotal || '?'}: <strong>{data.stageName}</strong>
                </Typography>
              ) : null}
            </Box>

            {stateMsg ? <Alert severity={stateMsg.severity}>{stateMsg.text}</Alert> : null}
            {success ? <Alert severity="success" role="status">{success}</Alert> : null}

            {draft ? (
              <>
                {data.intro ? <Typography variant="body2">{data.intro}</Typography> : null}
                {(view?.header || []).length ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1, p: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                    <Typography variant="body2"><strong>Raised by:</strong> {data.originatorName || '-'}</Typography>
                    {view.header.map((h: any, i: number) => (
                      <Typography key={`${h.label}-${i}`} variant="body2" sx={h.label === 'Remarks' ? { gridColumn: '1 / -1' } : undefined}>
                        <strong>{h.label}:</strong>{' '}
                        {h.href ? <Link href={h.href} target="_blank" rel="noopener noreferrer">{h.value}</Link> : h.value}
                      </Typography>
                    ))}
                  </Box>
                ) : null}

                {(view?.columns || []).length && (view?.rows || []).length ? (
                  <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflowX: 'auto' }}>
                    <Table size="small" aria-label={view.isService ? 'Purchase order service lines' : 'Purchase order lines'}>
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          {view.columns.map((c: any) => (
                            <TableCell key={c.key} align={c.align === 'right' ? 'right' : 'left'}>{c.label}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {view.rows.map((r: any, i: number) => (
                          <TableRow key={i}>
                            <TableCell>{i + 1}</TableCell>
                            {view.columns.map((c: any) => (
                              <TableCell key={c.key} align={c.align === 'right' ? 'right' : 'left'}>{r[c.key]}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={view.columns.length + 1} align="right" sx={{ fontWeight: 700 }}>
                            Document total: {money(draft.currency, draft.total)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : null}

                {(data.history || []).length ? (
                  <Box>
                    <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>Approval history</Typography>
                    <Stack spacing={0.5}>
                      {data.history.map((h: any, i: number) => (
                        <Typography key={i} variant="body2">
                          <strong>{h.stageName}</strong> – {h.approverName}: <strong>{decisionLabel(h.status, h.remarks)}</strong>
                          {h.date ? ` (${h.date})` : ''}{h.remarks ? ` – ${h.remarks}` : ''}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                ) : null}

                {(data.attachments || []).length ? (
                  <Box>
                    <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>Attachments</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {data.attachments.map((a: any) => (
                        <Chip
                          key={a.lineNum}
                          label={a.name}
                          component={Link}
                          href={apiService.sapPoApprovalAttachmentUrl(token, a.lineNum)}
                          clickable
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Box>
                ) : null}
              </>
            ) : null}

            {error ? <Alert severity="error" role="alert">{error}</Alert> : null}

            {data?.state === 'pending' && !success && !hasPassword ? (
              <Alert severity="warning">
                {data.password?.rejected
                  ? <>SAP no longer accepts the SAP B1 password PRPO has for user <strong>{data.approverUserCode}</strong> (it was changed or has expired).</>
                  : <>PRPO has no SAP B1 password for user <strong>{data.approverUserCode}</strong> yet.</>}
                {' '}Ask your PRPO administrator to set it up, then open this link again. You can also approve in SAP B1.
              </Alert>
            ) : null}

            {canDecide ? (
              <Box component="form" onSubmit={submit} sx={{ p: 2, border: '1px solid #cbd5e1', borderRadius: 2, bgcolor: '#fff' }}>
                <Stack spacing={2}>
                  <ToggleButtonGroup
                    exclusive
                    value={action}
                    onChange={(_e, v) => { if (v) { setAction(v); setError(''); } }}
                    aria-label="Decision"
                    sx={{ flexWrap: 'wrap' }}
                  >
                    {ACTIONS.map((a) => (
                      <ToggleButton key={a.value} value={a.value} color={a.color} sx={{ fontWeight: 700 }}>
                        {a.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <TextField
                    label={needsComment ? 'Comment for the initiator (required)' : 'Comment (optional)'}
                    value={comments}
                    onChange={(e) => setComments(e.target.value.slice(0, 220))}
                    multiline
                    minRows={2}
                    fullWidth
                    helperText={`${comments.length}/220 – saved in SAP B1 with your decision`}
                  />

                  <Typography variant="body2" color="text.secondary">
                    PRPO records your decision in SAP B1 as user <strong>{data.approverUserCode}</strong> ({data.approverName}).
                  </Typography>

                  <Box>
                    <Button
                      type="submit"
                      variant="contained"
                      color={ACTIONS.find((a) => a.value === action)?.color}
                      disabled={submitting}
                    >
                      {submitting ? 'Recording in SAP…' : `Confirm: ${ACTIONS.find((a) => a.value === action)?.label}`}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            ) : null}

            <Typography variant="caption" color="text.secondary">
              Your decision is recorded in SAP B1 under your own SAP user, exactly as if you approved in SAP. PRPO uses the SAP B1 password your
              administrator set up for you: it is encrypted, never shown, and SAP refusing it once stops its use. You can also approve directly in SAP B1.
            </Typography>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default SapPoApprovalAction;
